import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "../hooks/useTranslation";
import { useLanguage } from "../contexts/LanguageContext";

export default function Home() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  
  const coreMetrics = [
    {
      metric: "2",
      label: t('home.metrics.papers'),
      description: t('home.metrics.papersDesc')
    },
    {
      metric: t('home.metrics.accuracy'),
      label: t('home.metrics.accuracyLabel'),
      description: t('home.metrics.accuracyDesc')
    },
    {
      metric: t('home.metrics.projects'),
      label: t('home.metrics.projectsLabel'),
      description: t('home.metrics.projectsDesc')
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary via-secondary to-primary">
      {/* Hero Section */}
      <section className="section-padding min-h-screen flex items-center">
        <div className="container-width">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Profile Photo - Left Side */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:col-span-4 flex justify-center lg:justify-start order-2 lg:order-1"
            >
              <div className="relative">
                <div className="w-48 h-48 sm:w-64 sm:h-64 md:w-72 md:h-72 lg:w-80 lg:h-80 rounded-full overflow-hidden border-4 border-accent/30 shadow-2xl shadow-accent/20 ring-4 ring-accent/10">
                  <img 
                    src="/Anjalinewphoto.jpg" 
                    alt="Anjali Barge"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-2 -right-2 sm:-bottom-4 sm:-right-4 bg-accent text-primary rounded-full p-2 sm:p-3 shadow-lg">
                  <span className="text-lg sm:text-2xl">🚀</span>
                </div>
              </div>
            </motion.div>

            {/* Text Content - Right Side */}
            <div className="lg:col-span-8 order-1 lg:order-2">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="mb-6"
              >
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-2 leading-tight">
                  <span className="text-gradient">{t('home.title')}</span>
                </h1>
                
                {/* Katakana name - shown only in Japanese */}
                {language === 'ja' && (
                  <p className="text-lg sm:text-xl md:text-2xl text-accent/80 mb-4 font-light">
                    バルゲ アンジャリ サントーシュ
                  </p>
                )}
                
                <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-white mb-4 sm:mb-6">
                  {t('home.subtitle')}
                </h2>
                
                <div className="text-base sm:text-lg md:text-xl text-muted-light mb-4 sm:mb-6 leading-relaxed">
                  <p className="mb-3 sm:mb-4">
                    {t('home.description')}
                  </p>
                </div>

                {/* Core Metrics */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8"
                >
                {coreMetrics.map((item, index) => (
                    <div key={index} className="text-center sm:text-left p-3 sm:p-0">
                      <div className="text-2xl sm:text-2xl md:text-3xl font-bold text-accent mb-1 sm:mb-2">{item.metric}</div>
                      <div className="font-semibold text-white mb-1 text-sm sm:text-sm md:text-base">{item.label}</div>
                      <div className="text-xs sm:text-xs md:text-sm text-muted leading-tight">{item.description}</div>
                    </div>
                  ))}
                </motion.div>

                {/* CTA Buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8"
                >
                  <Link to="/research" className="btn-primary w-full sm:w-auto text-center touch-target">
                    {t('home.cta.research')}
                  </Link>

                  <Link to="/projects" className="btn-primary w-full sm:w-auto text-center touch-target">
                    {t('home.cta.projects')}
                  </Link>

                  <Link to="/resume" className="btn-secondary w-full sm:w-auto text-center touch-target">
                    {t('home.cta.resume')}
                  </Link>

                  <a 
                    href="https://github.com/AB2511"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary w-full sm:w-auto text-center touch-target"
                  >
                    {t('home.cta.github')}
                  </a>
                </motion.div>
              </motion.div>
            </div>
          </div>

          {/* Education Callout - Compact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-16"
          >
            <div className="card bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-l-4 border-accent max-w-4xl mx-auto">
              <div className="flex items-start gap-4">
                <div className="text-4xl">🇯🇵</div>
                <div className="flex-1">
                  <h3 className="font-bold text-white text-lg mb-2">{t('home.education.title')}</h3>
                  <p className="text-sm text-muted-light">
                    <strong className="text-accent">{t('home.education.university')}</strong><br/>
                    {t('home.education.program')}<br/>
                    {t('home.education.supervisor')}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>




        </div>
      </section>

      {/* Quick Links Section */}
      <section className="section-padding bg-surface-light/50">
        <div className="container-width">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">{t('home.exploreWork.title')}</h2>
            <p className="text-muted-light max-w-2xl mx-auto">
              {t('home.exploreWork.description')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: t('home.exploreWork.research.title'),
                description: t('home.exploreWork.research.desc'),
                link: "/research",
                icon: "🔬"
              },
              {
                title: t('home.exploreWork.aiProjects.title'),
                description: t('home.exploreWork.aiProjects.desc'),
                link: "/projects",
                icon: "🤖"
              },
              {
                title: t('home.exploreWork.skills.title'),
                description: t('home.exploreWork.skills.desc'),
                link: "/skills",
                icon: "⚡"
              },
              {
                title: t('home.exploreWork.about.title'),
                description: t('home.exploreWork.about.desc'),
                link: "/about",
                icon: "👋"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Link
                  to={item.link}
                  className="card card-hover block text-center group"
                >
                  <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-2 group-hover:text-accent transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-muted text-sm">{item.description}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}